/** @jsx jsx */

import type { KeyboardEvent } from 'react';
import { forwardRef, useCallback, useRef, useState } from 'react';
import { jsx } from '@balance-web/core';
import { useForkedRef } from '@balance-web/utils';
import { Tooltip } from '@balance-web/tooltip';
import type { TooltipProps } from '@balance-web/tooltip';
import { HelpCircleIcon } from '@balance-web/icon';
import { Inline } from '@balance-web/inline';

import type { SortDirection } from '../types';
import { siftAriaAttributes } from '../utils';

import { CellContent } from './CellContent';
import { SortIndicator } from './SortIndicator';
import type { TableCellProps, UseCellStylesProps } from './TableCell';
import { useCellStyles } from './TableCell';

export type TableColumnHeaderProps = Omit<TableCellProps, 'role'> &
  UseCellStylesProps & {
    /**
     * The callback to run when a user clicks the column header. Typically
     * reserved for sort behaviour.
     */
    onClick?: () => void;
    /**
     * The aria-sort attribute indicates if items in a table or grid are sorted in
     * ascending or descending order.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-sort
     */
    'aria-sort'?: SortDirection;
    tooltip?: Pick<TooltipProps, 'content'>['content'];
  };

export const TableColumnHeader = forwardRef<
  HTMLDivElement,
  TableColumnHeaderProps
>(({ children, id, onClick, tooltip, ...props }, forwardedRef) => {
  const { dataAttributes, css, style } = useCellStyles(props);
  const ariaAttributes = siftAriaAttributes(props);
  // in this case "active" means hovered or focused
  const [isActive, setActive] = useState(false);

  const internalRef = useRef<HTMLSpanElement>(null);
  const ref = useForkedRef(internalRef, forwardedRef);

  const onActivate = useCallback(() => {
    setActive(true);
  }, []);
  const onDeactivate = useCallback(() => {
    setActive(false);
  }, []);
  const onKeyDown = useCallback((event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      internalRef.current?.click();
    }
  }, []);

  // indicator
  const isSortActive = onClick && isActive;
  const sortIndicator = onClick && (
    <SortIndicator tone={isSortActive ? 'textPrimary' : 'textTertiary'} />
  );

  return (
    <div
      ref={ref}
      css={css}
      id={id}
      role="columnheader"
      style={style}
      tabIndex={onClick ? 0 : undefined}
      onBlur={onDeactivate}
      onClick={onClick}
      onFocus={onActivate}
      onKeyDown={onKeyDown}
      onMouseEnter={onActivate}
      onMouseLeave={onDeactivate}
      {...ariaAttributes}
      {...dataAttributes}
    >
      {props.align === 'right' && sortIndicator}

      <CellContent
        color={isSortActive ? 'textPrimary' : 'textSecondary'}
        transform="uppercase"
        weight="medium"
      >
        {children}
      </CellContent>
      {tooltip ? (
        <Tooltip content={tooltip}>
          <Inline aria-label="Help" cursor="pointer" paddingLeft="xsmall">
            <HelpCircleIcon color="textTertiary" size="small" />
          </Inline>
        </Tooltip>
      ) : null}

      {props.align !== 'right' && sortIndicator}
    </div>
  );
});
